import { z } from 'zod';
import {
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  listagemDeCadastroSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';
import { GRUPOS_CONTA_DO_CONTRATO } from './contratos.ts';
import { enderecoSchema, textoOpcional } from './endereco.ts';
import { MAXIMO_NUMERO_DOCUMENTO } from './titulos.ts';

/**
 * Obras e partes de obra (`.spec/010`, módulo Contratos; `.spec/016`, seção 4).
 *
 * A obra é **o centro de custo do sistema**: todo rateio de nota, de título e de
 * consumo aponta para uma delas. Por isso este cadastro é o que mais aparece em
 * combo de seleção no resto do sistema, e por isso o nome é único na conta —
 * duas linhas com o mesmo nome ali são indistinguíveis, e escolher a errada
 * erra o custo de uma obra inteira sem erro nenhum na tela.
 *
 * As **partes** viajam dentro do payload da obra, e não em rotas próprias. A
 * parte não existe sem a obra: separá-las permitiria gravar uma obra e falhar
 * ao gravar as etapas dela, deixando o cadastro pela metade sem que o usuário
 * soubesse qual metade.
 *
 * ## A obra também fatura
 *
 * Desde 18/09/2026 a obra gera contas a receber por conta própria, no molde do
 * contrato: `codigo`, `valor`, `contaContabilId` e `condicaoPagamentoId` são o
 * que o botão de gerar consome (`.spec/015`, decisão 55).
 *
 * O identificador curto chama-se **`codigo`**, e não `numero`, porque `numero`
 * já é o número do endereço — a obra herda `enderecoSchema`, como a pessoa. É
 * também o nome que `insumos` usa para a mesma ideia.
 *
 * O contrato não cobria três casos, e são os comuns: obra **sem contrato**
 * assinado — que o cadastro já previa, com `contratoId` e `clienteId`
 * independentes —, contrato **por medição**, cujo valor é nulo e que por
 * definição não gera parcela, e contrato que **agrupa várias obras**, onde o
 * rateio igualitário divide o valor global e não deixa faturar uma delas
 * sozinha. Nos três, o faturamento só existia digitado título a título.
 *
 * Os quatro campos são **opcionais no cadastro** — exceto `numero` —, pela
 * mesma razão que os do contrato: obra se cadastra no dia em que aparece, e
 * escopo, classificação contábil e prazo vêm depois. Quem os cobra é o ato de
 * gerar, com esta tela a um clique de distância.
 */

/* -------------------------------------------------------------------------- */
/* Cronograma — comum à obra e à parte                                        */
/* -------------------------------------------------------------------------- */

/** Tamanho máximo do nome. Vale para a obra e para a parte dela. */
export const TAMANHO_MAXIMO_NOME_OBRA = 200;

/**
 * As três datas, todas opcionais.
 *
 * Obra se cadastra no dia em que aparece, e o prazo costuma vir depois da
 * assinatura. Exigir data de conclusão no cadastro produziria palpite gravado
 * como fato — e palpite gravado não se distingue de data real seis meses depois.
 */
const cronogramaSchema = z.object({
  dataInicio: dataSchema.nullable().prefault(null),
  dataPrazo: dataSchema.nullable().prefault(null),
  dataConclusao: dataSchema.nullable().prefault(null),
});

type Cronograma = z.infer<typeof cronogramaSchema>;

/**
 * Prazo e conclusão nunca antes do início.
 *
 * Mesma regra do `CHECK obras_cronograma_coerente`, repetida aqui de propósito:
 * o `CHECK` recusa a linha com uma violação de constraint que o usuário não
 * consegue interpretar, e aqui o erro chega preso ao campo que a tela destaca.
 *
 * **O prazo não limita a conclusão.** Obra que atrasa conclui depois do prazo, e
 * é exatamente esse o dado que interessa medir. Uma validação entre os dois
 * impediria de gravar o caso que o cadastro existe para registrar.
 *
 * A comparação é de texto, e é correta porque `AAAA-MM-DD` ordena
 * lexicograficamente igual ao calendário. Converter para `Date` traria fuso para
 * dentro de uma data de negócio — e prazo é o dia, não um instante.
 */
function validarCronograma(dados: Cronograma, ctx: z.RefinementCtx, prefixo: (string | number)[]) {
  const { dataInicio: inicio, dataPrazo: prazo, dataConclusao: conclusao } = dados;
  if (inicio === null) return;

  if (prazo !== null && prazo < inicio) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...prefixo, 'dataPrazo'],
      message: 'O prazo não pode ser anterior ao início.',
    });
  }

  if (conclusao !== null && conclusao < inicio) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...prefixo, 'dataConclusao'],
      message: 'A conclusão não pode ser anterior ao início.',
    });
  }
}

/* -------------------------------------------------------------------------- */
/* Parte da obra                                                              */
/* -------------------------------------------------------------------------- */

/**
 * O que a tela envia por parte — uma etapa ou bloco da obra, com cronograma
 * próprio.
 *
 * `id` é **opcional na entrada**: quem já existe manda o seu, quem é novo vem
 * sem. É assim que a API distingue "acrescentei uma etapa" de "renomeei esta
 * etapa" sem que a tela precise mandar uma lista de operações.
 *
 * **Sem `numero`**, e é de propósito (`.spec/015`, decisão 64): a ordem das
 * etapas se edita, mas o que a carrega é a **ordem do array** — a primeira linha
 * é a etapa 1, a segunda é a 2. Aceitar o número como campo deixaria a tela
 * mandar 1, 2, 2 ou 1, 2, 4, e a recusa disso viraria validação em dois lugares.
 * Assim não há o que recusar: repetição e buraco não têm como ser expressos.
 *
 * É a mesma escolha do item do orçamento, e o efeito na tela é o que o usuário
 * espera — digitar 3 numa linha a leva para a terceira posição, e as de baixo
 * andam uma casa.
 */
export const parteDeObraEntradaSchema = cronogramaSchema
  .extend({
    id: uuidSchema.nullable().prefault(null),
    nome: z
      .string()
      .trim()
      .min(1, 'Informe o nome da parte.')
      .max(
        TAMANHO_MAXIMO_NOME_OBRA,
        `O nome tem no máximo ${TAMANHO_MAXIMO_NOME_OBRA} caracteres.`,
      ),
    /**
     * Opcional, como a da obra: o nome da etapa já diz o que ela é, e "Fundação"
     * não precisa de parágrafo explicativo para ser gravada.
     */
    descricao: textoOpcional(2000),
    ativo: z.boolean().prefault(true),
  })
  .superRefine((dados, ctx) => validarCronograma(dados, ctx, []));

export type ParteDeObraEntrada = z.infer<typeof parteDeObraEntradaSchema>;

export const parteDeObraSchema = z.object({
  id: uuidSchema,
  obraId: uuidSchema,
  /**
   * A ordem da etapa dentro da obra — 1, 2, 3… (`.spec/015`, decisões 61 e 64).
   *
   * **Só na leitura**, como o número do item do orçamento: `parteDeObraEntrada`
   * de propósito não o tem. Quem numera é o servidor, pela posição no array que
   * a tela enviou — e é o que faz a obra gerada herdar a ordem da proposta em
   * vez de aparecer em ordem alfabética, porque a geração passa as linhas na
   * ordem delas.
   *
   * As partes chegam **ordenadas por ele** e a numeração é contígua a partir de
   * 1, porque toda gravação a refaz. O formulário, por isso, não precisa guardar
   * o campo: a posição na lista diz o mesmo.
   */
  numero: z.number().int(),
  nome: z.string(),
  descricao: z.string().nullable(),
  dataInicio: dataSchema.nullable(),
  dataPrazo: dataSchema.nullable(),
  dataConclusao: dataSchema.nullable(),
  /**
   * A linha do orçamento que gerou esta parte (`.spec/015`, decisão 59).
   *
   * Nulo na parte digitada à mão, que continua sendo o caso da obra cadastrada
   * sem orçamento. Não entra no payload e **não muda na edição**: a origem de
   * uma etapa não se corrige renomeando-a.
   */
  orcamentoItemId: uuidSchema.nullable(),
  /**
   * O **previsto** da etapa: o valor da linha do orçamento, desnormalizado na
   * leitura.
   *
   * Não é coluna própria, e a escolha é a mesma do total do orçamento: um valor
   * copiado passa a viver por conta própria e é onde ele começa a discordar da
   * proposta que o cliente tem na mão. Aqui ele vem por junção, sempre igual ao
   * documento, e é contra ele que o consumo apropriado à parte será confrontado.
   *
   * Nulo quando a parte não veio de orçamento nenhum.
   */
  orcamentoItemValor: dinheiroSchema.nullable(),
  ativo: z.boolean(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type ParteDeObra = z.infer<typeof parteDeObraSchema>;

/* -------------------------------------------------------------------------- */
/* Obra                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Nomes de parte repetidos dentro da mesma obra.
 *
 * O `UNIQUE (tenant_id, obra_id, nome)` do banco já recusa, mas devolveria uma
 * violação de constraint sem dizer **qual** das linhas colidiu. Aqui o erro
 * chega no índice certo do array, e o sub-grid destaca a linha exata.
 *
 * A comparação ignora caixa e espaço nas pontas: "Fundação" e "fundação " são a
 * mesma etapa para quem lê a tela, e deixá-las passar criaria duas linhas que o
 * usuário não consegue diferenciar.
 */
function validarPartesDistintas(partes: ParteDeObraEntrada[], ctx: z.RefinementCtx): void {
  const vistos = new Map<string, number>();

  for (const [indice, parte] of partes.entries()) {
    const chave = parte.nome.trim().toLocaleLowerCase('pt-BR');
    const primeiro = vistos.get(chave);

    if (primeiro === undefined) {
      vistos.set(chave, indice);
      continue;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['partes', indice, 'nome'],
      message: 'Já existe uma parte com este nome nesta obra.',
    });
  }
}

/**
 * Valor a faturar da obra: positivo. Ver o campo, em `criarObraSchema`.
 *
 * Fora do `extend` porque o `refine` devolve um `ZodEffects`, e encadeá-lo
 * direto ali obrigaria o `.nullable()` a vir antes — invertendo a ordem, o
 * `refine` passaria a rodar sobre `null` e teria de tratá-lo.
 */
const valorDaObraSchema = dinheiroSchema.refine(
  (v) => Number(v) > 0,
  'O valor da obra deve ser maior que zero.',
);

/**
 * Os registros da obra que a nota fiscal de serviço leva (`.spec/015`,
 * decisão 88) — o grupo "obra" da NFS-e nacional.
 *
 * **Os quatro são opcionais, e nenhum é conferido.** São números emitidos por
 * quatro órgãos diferentes (Receita, conselho profissional, prefeitura e o
 * cadastro imobiliário nacional), cada um com o seu formato, e a obra se
 * cadastra antes de qualquer um deles existir — o CNO sai depois do alvará, a
 * ART depois do contrato. Quem cobra o que falta é a emissão.
 *
 * Texto, e não número: a ART tem letra em alguns conselhos, e a inscrição
 * imobiliária tem ponto, traço e barra conforme o município. O que se grava é o
 * que está no papel.
 */
export const TAMANHO_MAXIMO_REGISTRO_DA_OBRA = 30;

const camposFiscaisDaObra = {
  /** Cadastro Nacional de Obras (ou o CEI, nas obras anteriores a 2019). */
  cno: textoOpcional(TAMANHO_MAXIMO_REGISTRO_DA_OBRA),
  /** Anotação de Responsabilidade Técnica (ou o RRT, do arquiteto). */
  art: textoOpcional(TAMANHO_MAXIMO_REGISTRO_DA_OBRA),
  /** Inscrição do imóvel na prefeitura — a do carnê do IPTU. */
  inscricaoImobiliaria: textoOpcional(TAMANHO_MAXIMO_REGISTRO_DA_OBRA),
  /** Cadastro Imobiliário Brasileiro, o identificador nacional do imóvel. */
  cib: textoOpcional(TAMANHO_MAXIMO_REGISTRO_DA_OBRA),
};

export const criarObraSchema = enderecoSchema
  .merge(cronogramaSchema)
  .extend({
    /**
     * Código da obra. **Obrigatório e único na conta**, no máximo 30 caracteres.
     *
     * O teto é 30, e não os 200 do nome nem os 60 do número do contrato: é ele
     * que vira `numero_documento` do título gerado, e ali o teto é 30. O contrato
     * resolve a diferença **recusando a geração** (`numero_do_contrato_longo`),
     * porque o número dele já existia com 60 quando o financeiro apareceu. Aqui
     * o campo nasce depois da geração, então o problema não chega a existir: a
     * recusa acontece na digitação, com o campo na frente de quem errou.
     *
     * É texto, e não inteiro, pelo mesmo motivo do contrato: código de obra
     * carrega ano e sigla ("2026/014-RES"), e guardá-lo como número perderia o
     * zero à esquerda que os documentos têm impresso.
     *
     * **Não é sugerido nem gerado**, ao contrário de `insumos.codigo`. Lá o
     * cadastro abre com o próximo sequencial porque o catálogo tem centenas de
     * linhas e ninguém decora a codificação; obra se cadastra algumas vezes por
     * ano, e o código dela já existe no contrato e na pasta antes de existir no
     * sistema.
     */
    codigo: z
      .string()
      .trim()
      .min(1, 'Informe o código da obra.')
      .max(
        MAXIMO_NUMERO_DOCUMENTO,
        `O código tem no máximo ${MAXIMO_NUMERO_DOCUMENTO} caracteres — é ele que vai para o número do documento dos títulos gerados.`,
      ),
    /** Nome pelo qual a obra é conhecida. Único na conta. */
    nome: z
      .string()
      .trim()
      .min(1, 'Informe o nome da obra.')
      .max(
        TAMANHO_MAXIMO_NOME_OBRA,
        `O nome tem no máximo ${TAMANHO_MAXIMO_NOME_OBRA} caracteres.`,
      ),
    /**
     * O que a obra é, em texto livre — e **opcional**.
     *
     * O nome já identifica a obra; a descrição é o detalhe que se acrescenta
     * depois, quando o escopo fecha. Exigi-la no cadastro faria o usuário
     * repetir o nome no campo de baixo só para conseguir salvar, e um campo
     * preenchido por obrigação não informa nada a quem lê.
     */
    descricao: textoOpcional(2000),
    /**
     * Empresa do grupo que executa a obra. **Obrigatória**, e é a única das três
     * referências que é.
     *
     * Cliente é quem paga e pode faltar; empresa é quem executa, e o custo da
     * obra entra no resultado dela — obra sem empresa seria custo sem dono.
     *
     * É também de quem será o **depósito do canteiro**, quando a conta cria um:
     * depósito pertence a empresa, e sem este campo não haveria como decidir de
     * quem ele é.
     *
     * Continua obrigatória com o canteiro desligado (`.spec/015`, decisão 42) —
     * a razão acima nunca dependeu do depósito, e o parâmetro se religa a
     * qualquer momento.
     */
    empresaId: uuidSchema,
    /**
     * Contrato e cliente, os dois opcionais e **independentes**.
     *
     * Obra própria não tem nenhum dos dois; obra contratada tem os dois; obra
     * tocada para um cliente antes da assinatura tem só o cliente. Derivar o
     * cliente do contrato deixaria esse terceiro caso sem onde gravar de quem é
     * a obra — e ele é o começo de toda obra contratada.
     */
    contratoId: uuidSchema.nullable().prefault(null),
    clienteId: uuidSchema.nullable().prefault(null),
    /**
     * Valor a faturar: **positivo**, e opcional.
     *
     * Nulo em obra que ainda não fechou escopo, e em obra que fatura pelo
     * contrato — ali quem gera é ele, e um valor aqui produziria o mesmo
     * dinheiro cobrado duas vezes por dois documentos diferentes.
     *
     * Positivo porque obra não devolve dinheiro: o que volta ao cliente é
     * desconto na baixa, não título negativo.
     */
    valor: valorDaObraSchema.nullable().prefault(null),
    /**
     * A receita que esta obra representa. Só grupo `RECEITA`.
     *
     * Opcional no cadastro e **conferida de novo ao gerar** — entre gravar e
     * faturar ela pode ter sido inativada ou ganhado filhas, virando sintética.
     */
    contaContabilId: uuidSchema.nullable().prefault(null),
    /**
     * Em quantas vezes o valor se divide, e a cada quantos dias.
     *
     * Contados da **data base** escolhida no diálogo de gerar, que não é campo
     * do cadastro. Uso `RECEBIMENTO` ou `AMBOS`; conferido ao gerar.
     */
    condicaoPagamentoId: uuidSchema.nullable().prefault(null),
    ...camposFiscaisDaObra,
    ativo: z.boolean().prefault(true),
    /**
     * As partes, na íntegra.
     *
     * A lista é **o estado final**, não um delta: o que não vier é removido. A
     * alternativa — mandar só o que mudou — obrigaria a tela a rastrear
     * exclusões separadamente, e uma etapa apagada que não fosse anotada
     * continuaria no banco sem nunca mais aparecer no formulário.
     */
    partes: z.array(parteDeObraEntradaSchema).max(200).prefault([]),
  })
  .superRefine((dados, ctx) => {
    validarCronograma(dados, ctx, []);
    validarPartesDistintas(dados.partes, ctx);
  });

export type CriarObra = z.infer<typeof criarObraSchema>;
export const atualizarObraSchema = criarObraSchema;
export type AtualizarObra = z.infer<typeof atualizarObraSchema>;

export const obraSchema = z.object({
  id: uuidSchema,
  /**
   * Nulo **só em obra gravada antes de 18/09/2026**.
   *
   * O payload o exige, então toda gravação nova o traz; o nulo é a janela de
   * migração, não estado normal. A obra legada abre o formulário com o campo
   * vazio e só salva depois de preenchido, e a geração recusa enquanto isso
   * (`obra_sem_codigo`). Ver `.spec/015`, decisão 55.
   */
  codigo: z.string().nullable(),
  nome: z.string(),
  descricao: z.string().nullable(),
  empresaId: uuidSchema,
  /** Desnormalizado na leitura, pelo mesmo motivo do número do contrato. */
  empresaNome: z.string().nullable(),
  contratoId: uuidSchema.nullable(),
  /**
   * Número do contrato, desnormalizado na leitura.
   *
   * A listagem mostra a que contrato a obra pertence sem uma segunda consulta
   * por linha — que, numa página de 50, seriam 50 idas ao banco por uma coluna.
   */
  contratoNumero: z.string().nullable(),
  /**
   * O orçamento de que esta obra nasceu (`.spec/015`, decisão 59).
   *
   * Nulo na obra cadastrada à mão. A coluna mora aqui, e não em `orcamentos`,
   * pelo mesmo motivo de `contratos.orcamento_id` — ver o comentário em
   * `orcamentoSchema`.
   */
  orcamentoId: uuidSchema.nullable(),
  /** Número do orçamento, desnormalizado na leitura. */
  orcamentoNumero: z.number().int().nullable(),
  clienteId: uuidSchema.nullable(),
  clienteNome: z.string().nullable(),
  endereco: z.string().nullable(),
  numero: z.string().nullable(),
  complemento: z.string().nullable(),
  bairro: z.string().nullable(),
  cidadeId: uuidSchema.nullable(),
  /** Nome da cidade, pelo mesmo motivo do número do contrato. */
  cidadeNome: z.string().nullable(),
  cidadeUf: z.string().nullable(),
  cep: z.string().nullable(),
  dataInicio: dataSchema.nullable(),
  dataPrazo: dataSchema.nullable(),
  dataConclusao: dataSchema.nullable(),
  ativo: z.boolean(),
  /** String decimal, nunca number: ponto flutuante em dinheiro perde centavos. */
  valor: dinheiroSchema.nullable(),
  contaContabilId: uuidSchema.nullable(),
  /** Desnormalizados na leitura, pelo mesmo motivo do número do contrato. */
  contaContabilCodigo: z.string().nullable(),
  contaContabilDescricao: z.string().nullable(),
  condicaoPagamentoId: uuidSchema.nullable(),
  condicaoPagamentoDescricao: z.string().nullable(),
  /** Os quatro registros que a nota de serviço leva — ver `camposFiscaisDaObra`. */
  cno: z.string().nullable(),
  art: z.string().nullable(),
  inscricaoImobiliaria: z.string().nullable(),
  cib: z.string().nullable(),
  /**
   * Quantos títulos a obra já gerou. **Derivado**, nunca coluna.
   *
   * É o que abre e fecha o botão de gerar: zero, o botão aparece; mais que zero,
   * o faturamento está lançado. Uma coluna de flag diria a mesma coisa e poderia
   * discordar dos títulos que existem — é a escolha que o contrato já tinha
   * feito, e pela qual a obra "em dia" com zero títulos nunca chega a existir.
   *
   * Conta só `origem = 'OBRA'`. O título digitado à mão com rateio nesta obra é
   * receita dela e **não** é geração dela; contá-lo travaria o botão de uma obra
   * que nunca gerou nada.
   */
  qtdTitulosGerados: z.number().int().min(0),
  /**
   * Quanto as notas de serviço já faturaram por esta obra (`.spec/015`,
   * decisão 96): o serviço menos o desconto incondicionado das notas emitidas
   * que geraram contas a receber. `'0.00'` na obra que nunca teve nota.
   *
   * É o número do saldo a faturar da decisão 94, trazido para a leitura da
   * obra por causa da **listagem**: lá são dezenas de obras de uma vez, e a
   * rota do saldo responde uma por chamada. O saldo não vem pronto — é
   * `saldoAFaturar(valor, faturadoEmNotas)`, de `saldo-a-faturar.ts`, e a tela
   * o calcula com a mesma função que a emissão usa.
   *
   * Com `qtdTitulosGerados` maior que zero ele é sempre zero: a obra que gerou
   * as parcelas pelo cadastro não fatura por nota (decisão 90).
   *
   * **`null` para quem não vê o saldo a faturar** (decisões 97 e 100). A
   * leitura da obra é de catálogo, aberta a todo usuário da conta; o
   * faturamento, não. Sem `faturamento.saldo_a_faturar.ver` a API devolve
   * `null`, que é "você não vê" — e não `'0.00'`, que seria um número, e faria
   * a tela mostrar o valor inteiro da obra como saldo a faturar. Até a decisão
   * 100 a permissão era a de ver nota de serviço; a nota mudou de módulo, e o
   * saldo ganhou a dele para Contratos e Gerência continuarem a vê-lo.
   */
  faturadoEmNotas: dinheiroSchema.nullable(),
  /**
   * Algum título gerado já recebeu baixa.
   *
   * Decide o que o formulário deixa editar: com baixa, os cinco campos de
   * `CAMPOS_CONGELADOS_DA_OBRA` travam. A tela precisa saber disso **antes** de
   * o usuário digitar, não depois de o `PUT` recusar.
   */
  temTituloBaixado: z.boolean(),
  /**
   * O depósito do canteiro desta obra, ou `null` quando ela não tem um.
   *
   * **Nulo é estado normal desde a decisão 42**: a conta que não controla
   * estoque por local desliga a criação automática, e a obra nasce sem canteiro.
   * Antes dela o nulo já existia, raro — a migração da decisão 11 pulou as obras
   * cujo nome já estava ocupado por outro depósito da mesma empresa.
   *
   * É `canteiroId`, e não `temCanteiro`: o id responde a mesma pergunta e ainda
   * diz **qual**, que é o que uma tela precisa para levar o usuário até lá. Um
   * booleano obrigaria uma segunda consulta no dia em que alguém quisesse o
   * link.
   *
   * Só de leitura: quem cria e move o canteiro é o cadastro da obra, no
   * servidor. Mandá-lo no payload deixaria um `curl` apontar a obra para o
   * depósito de outra.
   */
  canteiroId: uuidSchema.nullable(),
  /**
   * As partes vêm sempre, inclusive na listagem.
   *
   * Não é peso: a obra tem etapas em ordem de unidades, e a consulta é uma só
   * para a página inteira. Omiti-las na lista e trazê-las só no detalhe daria
   * dois formatos de obra para o frontend tratar, e o `.length` na listagem
   * mostraria zero em obra que tem cinco etapas.
   */
  partes: z.array(parteDeObraSchema),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type Obra = z.infer<typeof obraSchema>;

export const listaObrasSchema = respostaListaSchema(obraSchema);
export type ListaObras = z.infer<typeof listaObrasSchema>;

/**
 * Listagem, com dois filtros além dos comuns.
 *
 * `contratoId` porque a pergunta natural na tela de contrato é "quais obras são
 * deste contrato?", e `clienteId` porque é assim que se responde ao telefone
 * quando o cliente liga. Nenhum dos dois sai do campo de busca: o nome da obra
 * não carrega nem o número do contrato nem o nome de quem a encomendou.
 */
export const listarObrasQuerySchema = listagemDeCadastroSchema.extend({
  /** Quais obras esta empresa do grupo executa. */
  empresaId: uuidSchema.optional(),
  contratoId: uuidSchema.optional(),
  clienteId: uuidSchema.optional(),
});

export type ListarObrasQuery = z.infer<typeof listarObrasQuerySchema>;

/**
 * Colunas de ordenação da listagem.
 *
 * A ordem padrão é `nome` **crescente** — ao contrário do contrato, que ordena
 * pelo mais recente. A diferença é o que cada tela responde: contrato se procura
 * pelo que foi assinado por último, obra se procura pelo nome, porque quem abre
 * a tela já sabe qual obra quer.
 */
export const COLUNAS_ORDENAVEIS_OBRA = [
  'codigo',
  'nome',
  'dataInicio',
  'dataPrazo',
  'dataConclusao',
  'createdAt',
] as const;

/** O texto que a listagem exibe quando a obra não tem nenhuma etapa cadastrada. */
export const SEM_PARTES = 'Sem partes';

/* -------------------------------------------------------------------------- */
/* Geração do financeiro                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Os grupos do plano de contas que a obra aceita.
 *
 * Um só — `RECEITA` —, e derivado da tabela do contrato em vez de escrito de
 * novo: obra gera **crédito**, e o lado do recebimento é o mesmo nos dois. Uma
 * segunda lista literal daria dois lugares para a mesma verdade discordar no
 * dia em que o plano de contas ganhasse um grupo.
 *
 * A obra não tem o que o contrato tem do outro lado: ela não paga ninguém. O
 * custo dela entra por nota de compra e por consumo, e nenhum dos dois passa
 * por aqui.
 */
export const GRUPOS_CONTA_DA_OBRA = GRUPOS_CONTA_DO_CONTRATO.RECEBIMENTO;

/**
 * Os campos que o título gerado **copia** — os que a baixa congela.
 *
 * Mudar qualquer um sem baixa apaga os títulos e reabre o botão por
 * consequência; com baixa, o `PUT` recusa. A lista vive aqui, e não só no
 * service, porque é ela que a tela usa para decidir o que desabilitar: uma tela
 * que deixasse digitar o que o servidor recusa seria pior do que uma que não
 * deixa.
 *
 * São **cinco**, e o contrato congela três (`CAMPOS_CONGELADOS_POR_BAIXA`, em
 * `contratos.ts`). A diferença não é descuido de um lado nem do outro: os cinco
 * daqui são exatamente os que a geração copia para dentro do título — código
 * vira `numero_documento`, cliente vira `cliente_id`, conta vira
 * `conta_contabil_id`, e valor e condição fazem as parcelas. Lá,
 * `contaContabilId` ficou de fora, e a consequência é o título gerado
 * classificado numa conta que o cadastro já não diz — sem onde corrigir, porque
 * título gerado não se edita pela tela. Não copiamos a omissão.
 */
export const CAMPOS_CONGELADOS_DA_OBRA = [
  'codigo',
  'valor',
  'clienteId',
  'contaContabilId',
  'condicaoPagamentoId',
] as const;

/**
 * O que o botão de gerar manda: **uma data**, e só.
 *
 * A data base é de onde a condição de pagamento conta os dias. Ela **não é
 * campo gravado da obra**, pela mesma razão do contrato: gravá-la faria o
 * cadastro carregar um dado que só serve ao instante da geração, e que ficaria
 * mentindo no dia em que os títulos fossem apagados e refeitos a partir de
 * outro dia.
 *
 * Nada mais entra — nem valor, nem condição, nem cliente. Todos são da obra, e
 * aceitá-los aqui abriria a porta para gerar títulos que não correspondem ao
 * cadastro que os originou.
 */
export const gerarFinanceiroDaObraSchema = z.object({
  dataBase: dataSchema,
});

export type GerarFinanceiroDaObra = z.infer<typeof gerarFinanceiroDaObraSchema>;

/**
 * Uma parcela como a geração a devolve — o resumo que a tela mostra.
 *
 * É a resposta do `POST`, e não a lista de títulos inteiros: quem acabou de
 * gerar quer conferir vencimento e valor, e os títulos estão na tela de contas
 * a receber, a um clique de distância.
 */
export const parcelaDaObraSchema = z.object({
  parcela: z.number().int().min(1),
  vencimento: dataSchema,
  valor: dinheiroSchema,
  /** Id do título a receber gerado. */
  tituloId: uuidSchema,
});

export type ParcelaDaObra = z.infer<typeof parcelaDaObraSchema>;

export const financeiroDaObraSchema = z.object({
  obra: obraSchema,
  parcelas: z.array(parcelaDaObraSchema),
});

export type FinanceiroDaObra = z.infer<typeof financeiroDaObraSchema>;

/* -------------------------------------------------------------------------- */
/* Vínculo de insumos com o canteiro                                          */
/* -------------------------------------------------------------------------- */

/**
 * Pendura os insumos de grupos escolhidos no canteiro da obra.
 *
 * **O buraco que isto fecha.** A obra nasce com o depósito do canteiro
 * (`garantirDepositoDaObra`), e ele nasce **vazio de vínculos**: nenhum insumo
 * aponta para ele. Quem cria os vínculos em massa é a importação do catálogo
 * padrão, que roda no provisionamento — quando ainda não existe obra nenhuma.
 * O resultado é que o canteiro criado depois não recebe material: a nota para
 * na confirmação com `insumo_nao_vinculado_ao_deposito`, cinco documentos
 * depois de a compra ter começado, e a saída manual é abrir insumo por insumo
 * num cadastro que tem centenas.
 *
 * **Por grupo, e não "todos".** O grupo é a unidade que o usuário reconhece —
 * "este canteiro não recebe serviço de terraplenagem" é uma frase que ele sabe
 * dizer, e "não recebe o insumo nº 217" não é. A tela marca todos por padrão,
 * porque o caso comum é o canteiro que trabalha com o catálogo inteiro.
 *
 * **O ramo inteiro entra.** Escolher "Materiais" leva "Materiais › Elétrica"
 * junto: é o que a palavra "dentro" significa na árvore que o usuário está
 * olhando. Quem resolve isso é o servidor, com `WITH RECURSIVE` — mandar os
 * descendentes pela requisição seriam centenas de uuid e deixaria a regra de
 * fora para quem chama a API direto.
 *
 * **Dois vínculos, e não um.** `insumos_empresa` para todo insumo do ramo, e
 * `insumos_deposito` só para material. É a decisão 10 da `.spec/015` escrita
 * como ação: insumo exige empresa (material e serviço), e um depósito em cada
 * empresa vinculada (só material). Vincular ao depósito sem vincular à empresa
 * destravaria a entrada da nota e continuaria barrando a solicitação, que é
 * onde a compra começa — o usuário trocaria um erro tardio por outro.
 *
 * **Não há campo de depósito no payload.** O destino é o canteiro daquela obra,
 * resolvido no servidor por `depositos.obra_id`. Aceitá-lo do cliente permitiria
 * mandar o depósito de outra obra numa rota cujo caminho já diz de quem é o
 * canteiro — e o nome do depósito é editável, então nem o nome serviria de
 * conferência.
 */
export const vincularInsumosDaObraSchema = z.object({
  /**
   * Os grupos escolhidos, **sem os descendentes**.
   *
   * A tela manda só os nós marcados de verdade: marcar "Materiais" cobre o ramo
   * abaixo, e mandar os cobertos junto seria repetir o que a recursão do
   * servidor já faz — com a diferença de que a lista da tela envelhece assim que
   * alguém cria um subgrupo, e a recursão não.
   */
  grupoIds: z.array(uuidSchema).min(1, 'Escolha ao menos um grupo de insumos.'),
});

export type VincularInsumosDaObra = z.infer<typeof vincularInsumosDaObraSchema>;

/**
 * O que o vínculo fez, em números.
 *
 * Os dois primeiros contam **linhas gravadas**, não insumos considerados: a
 * operação é idempotente, e repeti-la devolve zeros. Zero é resposta correta e
 * frequente — quem clicou duas vezes precisa ler "nada a fazer", não um erro.
 *
 * `insumosNoRamo` existe para dar sentido aos zeros. Sem ele, "0 vínculos" não
 * se distingue de "os grupos escolhidos estão vazios", que é uma situação
 * completamente diferente e se resolve escolhendo outros grupos.
 *
 * `servicosSemDeposito` é o número que explica a diferença entre os dois
 * primeiros contadores. Serviço não movimenta estoque, então entra na empresa e
 * não no depósito (`.spec/015`, decisão 9) — sem este número, quem escolheu um
 * grupo de serviços leria "12 na empresa, 0 no canteiro" como falha.
 */
export const resultadoVinculoDeInsumosSchema = z.object({
  /** Linhas de `insumos_empresa` gravadas, para a empresa que executa a obra. */
  vinculosEmpresaCriados: z.number().int(),
  /** Linhas de `insumos_deposito` gravadas, para o canteiro da obra. */
  vinculosDepositoCriados: z.number().int(),
  /** Insumos ativos encontrados nos grupos escolhidos e nos ramos abaixo deles. */
  insumosNoRamo: z.number().int(),
  /** Serviços do ramo: entraram na empresa e, de propósito, não no canteiro. */
  servicosSemDeposito: z.number().int(),
  /** Nome do canteiro no momento da operação, para a confirmação nomear o destino. */
  depositoNome: z.string(),
});

export type ResultadoVinculoDeInsumos = z.infer<typeof resultadoVinculoDeInsumosSchema>;
